import { Injectable, Injector } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { ApiHttp } from '@core/api/api-http';
import { OpportunityApiHttp } from '@core/api/opportunity-api-http';
import { commandEvent, CommandEvent } from '@shared/components/load/command/command.component';
import { AlertDialogService, AlertSize, AlertType } from '@shared/components/modal/services/alert-dialog.service';
import { ConfirmationDialogService, ConfirmationType } from '@shared/components/modal/services/confirmation-dialog.service';
import { OpportunityEventService } from '@features/opportunities/services/opportunity-event.service';
import { AliGroupQuote } from '@features/opportunities/models/opportunity';

export interface AliApplicationStatus {
  applicationId: number;
  opportunityId: string;
  statusCode: string;
  statusDescription: string;
  sumInsured: number;
  submittedDate: string;
}

export interface AliApplicationStatusResponse {
  errorMessage: string | null;
  success: boolean;
  applicationStatuses: AliApplicationStatus[];
}

@Injectable({
  providedIn: 'root'
})
export class AliGroupService {

  // OpportunityEventService is resolved lazily via the Injector to break a
  // OpportunityEventService -> OpportunitySummaryService -> AliGroupService ->
  // OpportunityEventService constructor cycle that Angular's DI now refuses to
  // resolve at construction time (NG0200). The reference is only used inside
  // handleQuoteSuccess() so deferring resolution is safe.
  constructor(
    private http: OpportunityApiHttp,
    private alertDialogService: AlertDialogService,
    private confirmationDialogService: ConfirmationDialogService,
    private readonly injector: Injector
  ) { }

  /**
   * Start a new ALI Group insurance quote
   * @param opportunityId The opportunity ID
   * @returns Observable with the quote URL
   */
  startQuote(opportunityId: string): Observable<string> {
    return this.http.post<string>(
      `opportunities/${opportunityId}/ali-group/start-quote`,
      {},
      {
        ...ApiHttp.getStandardOptions(),
        responseType: 'text'
      }
    );
  }

  /**
   * Replace an existing ALI Group insurance quote
   * @param opportunityId The opportunity ID
   * @returns Observable with the quote URL
   */
  replaceQuote(opportunityId: string): Observable<string> {
    return this.http.post<string>(
      `opportunities/${opportunityId}/ali-group/replace-quote`,
      {},
      {
        ...ApiHttp.getStandardOptions(),
        responseType: 'text'
      }
    );
  }

  /**
   * Get the application status for ALI Group insurance
   * @param opportunityId The opportunity ID
   * @returns Observable with application status details
   */
  getApplicationStatus(opportunityId: string): Observable<AliApplicationStatusResponse> {
    return this.http.get<AliApplicationStatusResponse>(
      `opportunities/${opportunityId}/ali-group/application-status`,
      ApiHttp.getStandardOptions()
    );
  }

  /**
   * Check if the broker is accredited for ALI Group
   * @param opportunityId The opportunity ID
   * @returns Observable with boolean indicating accreditation status
   */
  checkBrokerAccreditation(opportunityId: string): Observable<boolean> {
    return this.http.get<boolean>(
      `opportunities/${opportunityId}/ali-group/check-owner-accreditation`,
      ApiHttp.getStandardOptions()
    );
  }

  /**
   * Creates a command event observable for starting/replacing ALI Group quote
   * @param startQuoteRequest$ Subject to trigger the command
   * @param getOpportunityId Function that returns the current opportunity ID
   * @param getIsEditingQuote Function that returns whether user is editing an existing quote
   */
  createStartQuoteCommand(
    startQuoteRequest$: Subject<void>,
    getOpportunityId: () => string | null,
    getIsEditingQuote: () => boolean
  ): Observable<CommandEvent<string>> {
    return commandEvent(
      startQuoteRequest$,
      () => {
        const opportunityId = getOpportunityId();
        const isEditingQuote = getIsEditingQuote();

        if (!opportunityId) {
          return of('');
        }

        return isEditingQuote
          ? this.replaceQuote(opportunityId)
          : this.startQuote(opportunityId);
      }
    );
  }

  /**
   * Checks if user is editing an existing quote
   */
  isEditingQuote(aliQuote?: AliGroupQuote): boolean {
    return aliQuote?.operation === 'Replacement' ||
      (aliQuote?.operation === 'Creation' && aliQuote?.status === 'Success');
  }


  /**
   * Shows confirmation dialog and triggers quote command
   * @param startQuoteRequest$ Subject to trigger after confirmation
   * @param isEditingQuote Whether user is editing an existing quote
   */
  showConfirmationAndStart(
    startQuoteRequest$: Subject<void>,
    isEditingQuote: boolean
  ): void {
    this.confirmationDialogService.confirm(
      {
        title: isEditingQuote ? 'Edit quote' : 'Start quote',
        message: isEditingQuote
          ? 'Proceeding will overwrite your existing quote with ALI Group. Are you sure you want to continue?'
          : 'Are you sure you want to start a new insurance quote with ALI Group?',
        confirmLabel: isEditingQuote ? 'Continue' : 'Start Quote',
        cancelLabel: 'Cancel',
        type: ConfirmationType.Positive
      },
      () => {},
      of([]),
      () => startQuoteRequest$.next()
    );
  }

  /**
   * Handles successful quote creation
   */
  handleQuoteSuccess(url: string): void {
    window.open(url, '_blank');
    this.injector.get(OpportunityEventService).refreshData();
  }

  /**
   * Handles quote creation error
   */
  handleQuoteError(err: any, isEditingQuote: boolean): void {
    const infoHeader = 'Unable to process your request at this time.';
    const errorMessage = typeof err.error === 'string' 
      ? err.error 
      : 'Unable to start the ALI Group insurance quote. Please try again or contact support if the problem persists.';
    
    this.alertDialogService.confirm({
      title: isEditingQuote ? 'Edit quote failed' : 'Start quote failed',
      message: `${infoHeader}\n\n${errorMessage}\n\n`,
      confirmLabel: 'Close',
      type: AlertType.Positive,
      preformatted: true,
      size: AlertSize.Medium
    });
  }

  /**
   * Updates the consent for ALI Group Insurance
   * @param opportunityId The opportunity ID
   * @param consentForAliGroupInsurance The consent value
   * @returns Observable with the update result
   */
  setConsentForAliGroupInsurance(opportunityId: string, consentForAliGroupInsurance: boolean): Observable<any> {
    return this.http.put(
      `opportunities/${opportunityId}`,
      { consentForAliGroupInsurance: consentForAliGroupInsurance },
      ApiHttp.getStandardOptions()
    );
  }
}

